/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

/**
 * Runtime configuration. The deployed bundle is environment-agnostic: the stack writes `config.json` into the
 * web bucket, so one artefact serves every deployment. In dev there is no such file, so `VITE_*` variables stand in.
 */
export const AppConfigSchema = z.object({
  api_url: z.string().min(1),
  user_pool_id: z.string().min(1),
  user_pool_client_id: z.string().min(1),
  region: z.string().min(1),
  deployment: z.string().min(1),
});
export type AppConfig = z.infer<typeof AppConfigSchema>;

const fromEnv = (): unknown => ({
  api_url: '/api',
  user_pool_id: import.meta.env.VITE_USER_POOL_ID,
  user_pool_client_id: import.meta.env.VITE_USER_POOL_CLIENT_ID,
  region: import.meta.env.VITE_AWS_REGION,
  deployment: 'local',
});

/** A dev server answers an unknown path with index.html at status 200, so anything that does not parse counts as absent. */
const fromFile = async (): Promise<unknown> => {
  const response = await fetch(`${import.meta.env.BASE_URL}config.json`, { cache: 'no-store' }).catch(() => undefined);
  if (!response?.ok) {
    return undefined;
  }
  return response.json().catch(() => undefined);
};

export const loadConfig = async (): Promise<AppConfig> => {
  const fileConfig = AppConfigSchema.safeParse(await fromFile());
  if (fileConfig.success) {
    return fileConfig.data;
  }
  const envConfig = AppConfigSchema.safeParse(fromEnv());
  if (envConfig.success) {
    return envConfig.data;
  }
  throw new Error(
    'No usable configuration. Deployed builds read config.json from the web bucket; ' +
      'for local development copy .env.example to .env.local and fill in the stack outputs.'
  );
};
